import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UnlockedAchievement } from '../types/profile';
import { getAchievementById } from '../data/achievements';

interface AchievementNotificationProps {
  achievements: UnlockedAchievement[];
  onDismiss: () => void;
}

export const AchievementNotification: React.FC<AchievementNotificationProps> = ({
  achievements,
  onDismiss
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (achievements.length > 0) {
      setIsVisible(true);
      setCurrentIndex(0);
    }
  }, [achievements]);

  useEffect(() => {
    if (!isVisible || achievements.length === 0) return;

    const timer = setTimeout(() => {
      if (currentIndex < achievements.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setIsVisible(false);
        setTimeout(onDismiss, 300); // Wait for exit animation
      }
    }, 3000); // Show each achievement for 3 seconds

    return () => clearTimeout(timer);
  }, [currentIndex, isVisible, achievements.length, onDismiss]);

  if (achievements.length === 0) return null;

  const currentAchievement = getAchievementById(achievements[currentIndex]?.id);
  if (!currentAchievement) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -100, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -100, scale: 0.8 }}
          transition={{ 
            type: "spring", 
            stiffness: 300, 
            damping: 25,
            duration: 0.5 
          }}
          className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50"
        >
          <div className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 p-1 rounded-xl shadow-2xl">
            <div className="bg-white rounded-lg p-4 min-w-[320px] max-w-[400px]">
              <div className="flex items-center space-x-3">
                <motion.div
                  initial={{ rotate: 0, scale: 1 }}
                  animate={{ 
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    duration: 0.6,
                    repeat: Infinity,
                    repeatDelay: 2
                  }}
                  className="text-4xl"
                >
                  {currentAchievement.icon}
                </motion.div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="font-bold text-gray-800 text-lg">
                      Achievement Unlocked!
                    </h3>
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3 }}
                      className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium"
                    >
                      +{currentAchievement.points} pts
                    </motion.div>
                  </div>
                  
                  <h4 className="font-semibold text-gray-700 mb-1">
                    {currentAchievement.name}
                  </h4>
                  
                  <p className="text-sm text-gray-600">
                    {currentAchievement.description}
                  </p>
                </div>
              </div>

              {/* Progress indicator for multiple achievements */}
              {achievements.length > 1 && (
                <div className="mt-3 flex justify-center space-x-1">
                  {achievements.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                        index === currentIndex 
                          ? 'bg-orange-400' 
                          : index < currentIndex 
                            ? 'bg-green-400' 
                            : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* Dismiss button */}
              <button
                onClick={() => {
                  setIsVisible(false);
                  setTimeout(onDismiss, 300);
                }}
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Sparkle effects */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 pointer-events-none"
          >
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ 
                  opacity: 0, 
                  scale: 0,
                  x: Math.random() * 300 - 150,
                  y: Math.random() * 200 - 100
                }}
                animate={{ 
                  opacity: [0, 1, 0], 
                  scale: [0, 1, 0],
                  y: [0, -50]
                }}
                transition={{ 
                  duration: 2,
                  delay: i * 0.2,
                  repeat: Infinity,
                  repeatDelay: 3
                }}
                className="absolute text-yellow-400 text-lg"
                style={{
                  left: `${20 + (i * 15)}%`,
                  top: `${30 + (i % 2) * 40}%`
                }}
              >
                ✨
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}; 